// frontend/src/pages/SupplierDashboard.js  (replacement)
// Same data layer as before — available RFQs and /quotations/supplier/stats.
// Only the presentation changed: chrome/ink panels, one divided stat bar
// instead of four coloured gradient cards, stroke icons instead of emoji.

import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../context/AuthContext';
import API_URL from '../api/baseURL';
import { Page, Panel, Button, Badge, EmptyState } from '../components/ui/Iparc';
import { fmt } from '../theme/iparc';
import { IconDoc, IconCheck, IconCard, IconClock, IconBox, IconChevron } from '../components/icons/Icons';

const num = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };

// Mirrors QuotationResponse.status. Kept in one place so the queue and the
// activity list cannot label the same status differently.
const STATUS = {
  accepted: { tone: 'ok', label: 'Accepté' },
  submitted: { tone: 'warn', label: 'En attente' },
  rejected: { tone: 'danger', label: 'Rejeté' },
  client_countered: { tone: 'neutral', label: 'Contre-offre' },
};

const StatCell = ({ label, icon, children }) => (
  <div className="bg-gradient-to-b from-[#17140F] to-[#131110] px-[18px] py-4">
    <div className="flex items-center justify-between gap-2">
      <span className="text-[10.5px] uppercase tracking-[.16em] text-[#6E665C] whitespace-nowrap">{label}</span>
      <span className="text-[#4E4740]">{icon}</span>
    </div>
    {children}
  </div>
);

const Figure = ({ value, unit, hint }) => (
  <>
    <div className="flex items-end gap-1.5 mt-3">
      <span className="font-['Space_Grotesk',sans-serif] text-[30px] font-semibold leading-none tracking-[-.03em] tabular-nums text-[#F4F1EA] whitespace-nowrap">
        {value}
      </span>
      {unit && <span className="text-[13px] text-[#8F877B] pb-0.5">{unit}</span>}
    </div>
    {hint && <p className="text-[10.5px] text-[#5C554C] mt-2 leading-[1.45]">{hint}</p>}
  </>
);

const StatBarSkeleton = () => (
  <div className="grid gap-px rounded-2xl border border-[#241F1B] bg-[#241F1B] overflow-hidden
                  [grid-template-columns:repeat(auto-fit,minmax(196px,1fr))]">
    {[0, 1, 2, 3].map((i) => (
      <div key={i} className="bg-gradient-to-b from-[#17140F] to-[#131110] px-[18px] py-4">
        <div className="h-[11px] w-20 rounded bg-[#241F1B] animate-pulse" />
        <div className="h-[30px] w-24 rounded bg-[#241F1B] mt-3 animate-pulse" />
      </div>
    ))}
  </div>
);

const vehicleName = (source) =>
  [source?.car?.brand || source?.vehicleSnapshot?.make,
   source?.car?.model || source?.vehicleSnapshot?.model]
    .filter(Boolean).join(' ') || 'Véhicule';

// The VIN decides which variant of a filter, belt or injector actually fits, so
// the supplier needs it to price the line — not only after opening the request.
// vinSnapshot is the fallback for the same reason as vehicleSnapshot above: it
// is what the RFQ remembers if the vehicle is later renamed or deleted.
const vehicleVin = (source) =>
  String(source?.car?.vin || source?.vinSnapshot || '').trim().toUpperCase();

export default function SupplierDashboard({ token: tokenProp }) {
  const navigate = useNavigate();
  const { token: ctxToken, user } = useAuth();
  // The route passes a token prop; fall back to context so the page also works
  // when rendered without one.
  const token = tokenProp || ctxToken;

  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);

  useEffect(() => {
    if (!token) return;
    const h = { headers: { Authorization: `Bearer ${token}` } };

    axios.get(`${API_URL}/spare-parts/available`, h)
      .then((res) => setRequests(Array.isArray(res.data) ? res.data : []))
      .catch((err) => console.error('Error loading supplier requests', err))
      .finally(() => setLoading(false));

    axios.get(`${API_URL}/quotations/supplier/stats`, h)
      .then((res) => setStats(res.data))
      .catch((err) => console.error('Error loading stats', err))
      .finally(() => setStatsLoading(false));
  }, [token]);

  const greeting = (() => {
    const h = new Date().getHours();
    const name = user?.businessName || user?.name?.split(' ')[0] || 'Fournisseur';
    if (h < 12) return `Bonjour ${name}`;
    if (h < 18) return `Bon après-midi ${name}`;
    return `Bonsoir ${name}`;
  })();

  const accepted = num(stats?.acceptedCount);
  const total = num(stats?.totalQuotes);

  return (
    <Page
      subtitle={greeting}
      title="Votre activité aujourd’hui"
      actions={
        <>
          <Button onClick={() => navigate('/supplier/invoices')}>Mes factures</Button>
          <Button variant="primary" onClick={() => navigate('/supplier/accepted')}>
            <IconCheck className="w-3.5 h-3.5 shrink-0" /> Devis acceptés
          </Button>
        </>
      }
    >
      {statsLoading ? (
        <StatBarSkeleton />
      ) : (
        <div className="grid gap-px rounded-2xl border border-[#241F1B] bg-[#241F1B] overflow-hidden
                        [grid-template-columns:repeat(auto-fit,minmax(196px,1fr))]">
          <StatCell label="Devis envoyés" icon={<IconDoc className="w-[15px] h-[15px]" />}>
            <Figure
              value={total}
              hint={`${num(stats?.thisMonth?.quotes)} ce mois`}
            />
          </StatCell>

          <StatCell label="Taux d’acceptation" icon={<IconCheck className="w-[15px] h-[15px]" />}>
            <Figure value={`${num(stats?.acceptanceRate)}`} unit="%" />
            {/* The bar carries the same number as the figure — it is the
                proportion, not a second unrelated metric. */}
            <div className="h-[5px] rounded-[3px] bg-[#241F1B] overflow-hidden mt-3">
              <span
                className="block h-full rounded-[3px]"
                style={{ width: `${Math.min(100, num(stats?.acceptanceRate))}%`, background: '#74A98D' }}
              />
            </div>
            <p className="text-[10.5px] text-[#5C554C] mt-[7px] tabular-nums">
              {accepted} accepté{accepted > 1 ? 's' : ''} sur {total}
            </p>
          </StatCell>

          <StatCell label="Revenus" icon={<IconCard className="w-[15px] h-[15px]" />}>
            <Figure
              value={fmt(stats?.totalRevenue)}
              unit="F"
              hint={`${fmt(stats?.thisMonth?.revenue)} F ce mois`}
            />
          </StatCell>

          <StatCell label="En attente" icon={<IconClock className="w-[15px] h-[15px]" />}>
            <Figure
              value={num(stats?.pendingCount)}
              hint={`${num(stats?.rejectedCount)} rejeté${num(stats?.rejectedCount) > 1 ? 's' : ''}`}
            />
          </StatCell>
        </div>
      )}

      <div className="flex flex-wrap gap-3.5 items-start">
        {/* Available RFQs — the queue that actually earns money, so it leads. */}
        <section className="flex-[2] min-w-0 basis-[420px] rounded-2xl border border-[#241F1B]
                            bg-gradient-to-b from-[#17140F] to-[#131110] overflow-hidden">
          <header className="flex items-center justify-between gap-3 px-[18px] py-3.5 border-b border-[#241F1B]">
            <p className="text-[10.5px] uppercase tracking-[.16em] text-[#6E665C]">
              Demandes de devis
            </p>
            {requests.length > 0 && (
              <Badge tone="warn">{requests.length} à traiter</Badge>
            )}
          </header>

          {loading ? (
            <div className="p-[18px] flex flex-col gap-2">
              {[0, 1, 2].map((i) => (
                <div key={i} className="h-[74px] rounded-xl bg-[#1B1815] animate-pulse" />
              ))}
            </div>
          ) : requests.length === 0 ? (
            <div className="p-[18px]">
              <EmptyState
                title="Aucune demande en attente"
                hint="Les nouvelles demandes de devis apparaîtront ici."
              />
            </div>
          ) : (
            <div className="max-h-[420px] overflow-y-auto">
              {requests.map((req) => (
                <div
                  key={req._id}
                  className="flex items-center gap-3 px-[18px] py-3.5 border-b border-[#1E1B18] last:border-b-0
                             hover:bg-[#1A1613] transition-colors"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline gap-2">
                      <p className="font-['Space_Grotesk',sans-serif] text-[14px] font-medium text-[#F4F1EA] truncate">
                        {vehicleName(req)}
                      </p>
                      <span className="text-[11.5px] text-[#6E665C] tabular-nums shrink-0">
                        {req.car?.year || req.vehicleSnapshot?.year || '—'}
                      </span>
                    </div>

                    <div className="flex flex-wrap gap-1.5 mt-1.5">
                      {req.partsRequested?.slice(0, 3).map((part, i) => (
                        <span key={i}
                          className="text-[10.5px] text-[#C9C3B6] bg-[#1B1815] border border-[#241F1B]
                                     rounded-[6px] px-1.5 py-[2px]">
                          {part.name}
                        </span>
                      ))}
                      {req.partsRequested?.length > 3 && (
                        <span className="text-[10.5px] text-[#6E665C] px-1 py-[2px]">
                          +{req.partsRequested.length - 3}
                        </span>
                      )}
                    </div>

                    <p className="text-[10.5px] text-[#5C554C] mt-1.5 tabular-nums">
                      {req.createdAt && new Date(req.createdAt).toLocaleDateString('fr-FR')}
                      {req.car?.plateNumber && ` · ${req.car.plateNumber}`}
                    </p>

                    {vehicleVin(req) && (
                      <p className="text-[10.5px] text-[#6E665C] mt-0.5 tracking-[.06em] break-all">
                        <span className="text-[#5C554C]">VIN </span>
                        <span className="font-['Space_Grotesk',sans-serif] text-[#8F877B]">
                          {vehicleVin(req)}
                        </span>
                      </p>
                    )}
                  </div>

                  <Button variant="primary" onClick={() => navigate(`/respond-quotation/${req._id}`)}>
                    Répondre
                  </Button>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Recent activity */}
        <section className="flex-1 min-w-0 basis-[300px] rounded-2xl border border-[#241F1B]
                            bg-gradient-to-b from-[#17140F] to-[#131110] overflow-hidden">
          <header className="px-[18px] py-3.5 border-b border-[#241F1B]">
            <p className="text-[10.5px] uppercase tracking-[.16em] text-[#6E665C]">Activité récente</p>
          </header>

          {statsLoading ? (
            <div className="p-[18px] flex flex-col gap-2">
              {[0, 1, 2].map((i) => (
                <div key={i} className="h-[52px] rounded-xl bg-[#1B1815] animate-pulse" />
              ))}
            </div>
          ) : !stats?.recentActivity?.length ? (
            <div className="p-[18px]">
              <EmptyState title="Aucune activité" hint="Vos devis envoyés apparaîtront ici." />
            </div>
          ) : (
            <div>
              {stats.recentActivity.map((activity, idx) => {
                const s = STATUS[activity.status] || { tone: 'neutral', label: activity.status };
                return (
                  <div key={idx}
                    className="px-[18px] py-3 border-b border-[#1E1B18] last:border-b-0">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-[13px] text-[#F4F1EA] truncate">
                        {vehicleName(activity.rfq)}
                      </p>
                      <Badge tone={s.tone}>{s.label}</Badge>
                    </div>
                    <div className="flex items-center justify-between gap-2 mt-1">
                      <span className="text-[10.5px] text-[#5C554C] tabular-nums">
                        {activity.createdAt && new Date(activity.createdAt).toLocaleDateString('fr-FR')}
                      </span>
                      <span className="text-[11.5px] text-[#C9C3B6] tabular-nums whitespace-nowrap">
                        {fmt(activity.totalPublic || activity.totalSupplier || 0)} F
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </div>

      <Panel
        title="Raccourcis"
        bodyClass="p-[18px] grid gap-2.5 [grid-template-columns:repeat(auto-fit,minmax(220px,1fr))]"
      >
        {[
          { label: 'Devis acceptés', icon: IconCheck, to: '/supplier/accepted' },
          { label: 'Mes factures', icon: IconCard, to: '/supplier/invoices' },
          { label: 'Bons de commande', icon: IconBox, to: '/supplier/purchase-orders' },
        ].map(({ label, icon: Icon, to }) => (
          <button
            key={to}
            onClick={() => navigate(to)}
            className="flex items-center gap-2.5 px-3.5 py-3 rounded-xl border border-[#241F1B]
                       bg-[#1B1815] hover:border-[#4E4740] text-left transition-colors group"
          >
            <Icon className="w-[17px] h-[17px] shrink-0 text-[#8F877B] group-hover:text-[#F4F1EA] transition-colors" />
            <span className="flex-1 text-[13px] text-[#C9C3B6] group-hover:text-[#F4F1EA] transition-colors">
              {label}
            </span>
            <IconChevron className="w-3.5 h-3.5 shrink-0 text-[#4E4740]" />
          </button>
        ))}
      </Panel>
    </Page>
  );
}
